import type { ReactNode } from 'react'
import { Check, Circle, Pencil } from 'lucide-react'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { cn } from '@klear/ui/lib/utils'
import type { ExtractionField } from '@/app/document-review/approval-items'
import { ExtractionReceipt } from '@/app/document-review/extraction-receipt'
import { doubtKindLabel } from '@/app/document-review/review-labels'
import type { DocumentReviewDoubtView } from '@/app/document-review/use-document-review'

export type ApprovalRowProps = {
  approvalKey: string
  label: string
  shown: string
  shownContent?: ReactNode
  empty?: boolean
  required?: boolean
  original?: string | null
  extraction?: ExtractionField | null
  doubts?: readonly DocumentReviewDoubtView[]
  detail?: ReactNode
  confirmed: boolean
  needsApproval: boolean
  readOnly: boolean
  editable?: boolean
  editing?: boolean
  editor?: ReactNode
  issue?: string | null
  confirmLabel?: string
  onToggle: () => void
  onEdit?: () => void
}

export function approvalKeyOf(target: EventTarget | null): string | null {
  if (!(target instanceof HTMLElement)) return null
  const row = target.closest<HTMLElement>('[data-approval-key]')
  return row?.dataset['approvalKey'] ?? null
}

export function doubtLine(doubt: DocumentReviewDoubtView): string {
  if (doubt.kind !== 'missing_required_value') return doubt.message
  const hint = doubt.message.split(' — ')[1]
  if (hint === undefined) return doubt.message
  return `The run left this empty. Look for what ${hint}.`
}

export function ApprovalRow({
  approvalKey,
  label,
  shown,
  shownContent,
  empty = false,
  required = false,
  original = null,
  extraction = null,
  doubts = [],
  detail,
  confirmed,
  needsApproval,
  readOnly,
  editable = false,
  editing = false,
  editor,
  issue = null,
  confirmLabel = 'Confirm',
  onToggle,
  onEdit
}: ApprovalRowProps) {
  const interactive = !readOnly
  const confirmable = interactive && needsApproval
  const showEdit = interactive && editable && !editing && onEdit !== undefined

  return (
    <div
      data-approval-key={approvalKey}
      tabIndex={interactive ? 0 : -1}
      role={confirmable ? 'checkbox' : undefined}
      aria-checked={confirmable ? confirmed : undefined}
      aria-label={confirmable ? `${confirmLabel} ${label}: ${shown}` : undefined}
      onDoubleClick={showEdit ? onEdit : undefined}
      className={cn(
        'group grid grid-cols-[1.5rem_10rem_minmax(0,1fr)] items-start gap-x-2 px-3 py-2 outline-none',
        interactive && 'focus:bg-accent/50 focus:ring-1 focus:ring-ring focus:ring-inset',
        interactive && 'hover:bg-accent/30'
      )}
    >
      <div className="-my-1 -ml-1">
        {confirmable ? (
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.currentTarget.closest<HTMLElement>('[data-approval-key]')?.focus()
              onToggle()
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full"
          >
            {confirmed ? (
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : (
              <Circle className="text-muted-foreground/60 h-3.5 w-3.5" />
            )}
          </button>
        ) : confirmed ? (
          <span className="flex h-6 w-6 items-center justify-center">
            <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden />
            <span className="sr-only">Confirmed</span>
          </span>
        ) : (
          <span className="block h-6 w-6" aria-hidden />
        )}
      </div>

      <div className="text-muted-foreground flex flex-wrap items-baseline gap-x-1.5 pt-0.5 text-xs leading-5">
        <span>{label}</span>
        {required && empty && interactive && (
          <Badge variant="outline" className="px-1.5 py-0 text-[11px] font-normal text-amber-700 dark:text-amber-400">
            Required
          </Badge>
        )}
      </div>

      <div className="min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              'text-sm leading-5 break-words',
              empty ? 'text-muted-foreground italic' : 'font-medium',
              confirmed && !empty && original === null && 'text-foreground/75'
            )}
          >
            {shownContent ?? shown}
            {original !== null && (
              <>
                <span className="sr-only">, replacing</span>
                <del className="text-muted-foreground ml-2 text-xs font-normal">{original}</del>
              </>
            )}
          </span>
          {showEdit && (
            <Button
              variant="ghost"
              size="xs"
              tabIndex={-1}
              aria-label={`Correct ${label}`}
              onClick={onEdit}
              className="-my-0.5 shrink-0 opacity-40 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </Button>
          )}
        </div>
        {extraction !== null && <ExtractionReceipt extraction={extraction} className="mt-0.5" />}
        {detail}
        {doubts.map((doubt, index) => (
          <p key={`${doubt.kind}-${index}`} className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            <span className="font-medium">{doubtKindLabel(doubt.kind)}.</span> {doubtLine(doubt)}
            {doubt.proposedAnswer !== null && (
              <span className="text-foreground"> Proposed answer: {doubt.proposedAnswer}</span>
            )}
          </p>
        ))}
        {editing && editor}
        {issue !== null && <p className="text-destructive mt-1 text-xs">{issue}</p>}
      </div>
    </div>
  )
}
